// review-item-bodies — the rich per-kind review renderers.
//
// Extracted from the former ReviewQueueView page so they can be hosted both
// inline and inside the Kanban's <ReviewItemPanel> drawer. Each body owns the
// approve / reject / request-revision dispatch for its item kind:
//
//   - CallDraftsBody      → /api/agent/reviewables/call-* (+ "approve all")
//   - MonthlyNarrativeBody → /api/agent/reviewables/monthly-narrative-draft
//   - ComposerDraftBody    → the per-agent composition workbench (owns its own
//                            approve / reject / request-revision)
//   - TaskBody             → AgentContext.reviewTask (PATCH /api/agent/tasks/:id/approve|reject)
//
// Adding a new artefact type? Extend `useReviewQueue` with the item shape and
// add a renderer here.

import type { AgentTask } from '@septima/contract';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Quote,
    RotateCcw,
    ThumbsDown,
    ThumbsUp,
    XCircle,
} from 'lucide-react';
import { Pill, Button } from '@/components/ui';
import EmptyState from '@/components/EmptyState';
import { useAgent } from '@/contexts/AgentContext';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
    approveReviewable,
    rejectReviewable,
    requestRevisionReviewable,
} from '@/lib/agent-reviewables';
import { useToast } from '@/lib/useToast';
import { cn } from '@/lib/utils';
import {
    summariseArtefact,
    type CallDraftsReviewItem,
    type ComposerDraftReviewItem,
    type MonthlyNarrativeReviewItem,
    type TaskReviewItem,
} from '@/lib/use-review-queue';
import type { BaseDraft } from '@/data/types';
import CitedSummary from '../CitedSummary';
import { DownloadRow } from '../AgentTaskCard';
import { ExportToSharePointButton } from '../ExportToSharePointButton';
import RejectionDialog from '../RejectionDialog';
import ScrivenerWorkbench from '../composition-workbench/scrivener';
import HeraldWorkbench from '../composition-workbench/herald';
import EmissaryWorkbench from '../composition-workbench/emissary';
import CharterWorkbench from '../composition-workbench/charter';
import HavenWorkbench from '../composition-workbench/haven';
import ForumWorkbench from '../composition-workbench/forum';
import ArbiterWorkbench from '../composition-workbench/arbiter';

type ReviewStatus = 'pending' | 'approved' | 'rejected';

interface ArtefactRow {
    id: string;
    callId: string;
    clientId: string | null;
    reviewStatus?: ReviewStatus | null;
}

interface FactRow extends ArtefactRow {
    category: string;
    text: string;
    source_quote: string;
    source_timestamp: string;
    confidence: 'high' | 'medium' | 'low';
    compliance_relevant: boolean;
    compliance_reason: string | null;
}

interface ActionRow extends ArtefactRow {
    title: string;
    owner: string;
    due_description: string;
    note: string | null;
}

interface NoteRow extends ArtefactRow {
    title: string;
    body: string;
    tags: string[];
}

interface NarrativeDraftRow {
    id: string;
    clientId: string;
    period: string;
    selectedVariantIndex: number;
    performanceSummary: string;
    narrativeParagraph: string;
    variants: Array<{
        tone: string;
        performanceSummary: string;
        narrativeParagraph: string;
    }>;
    kbNotesCited: string[];
    reviewStatus?: ReviewStatus | null;
}

// -------- Call drafts body --------

export function CallDraftsBody({
    item,
    showReviewed,
}: {
    item: CallDraftsReviewItem;
    showReviewed: boolean;
}) {
    const useAgentRows = <T,>(table: string, key: string) =>
        useQuery<T[]>({
            queryKey: ['agent', 'rows', key],
            queryFn: async () => {
                const res = await api.agent.listAgentRows({
                    params: { table },
                    query: {},
                });
                if (res.status !== 200) throw new Error(`fetch ${res.status}`);
                return res.body.rows as unknown as T[];
            },
            refetchInterval: 5_000,
        });
    const allFacts =
        useAgentRows<FactRow>('call_extracted_facts', 'call_extracted_facts').data ?? [];
    const allActions = useAgentRows<ActionRow>('call_action_items', 'call_action_items').data ?? [];
    const allNotes = useAgentRows<NoteRow>('call_kb_notes', 'call_kb_notes').data ?? [];
    const { pushToast } = useToast();

    const include = (status: ReviewStatus | null | undefined): boolean => {
        if (showReviewed) return true;
        return status == null || status === 'pending';
    };

    const facts = useMemo(
        () => allFacts.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allFacts, item.callId, showReviewed],
    );
    const actions = useMemo(
        () => allActions.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allActions, item.callId, showReviewed],
    );
    const notes = useMemo(
        () => allNotes.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allNotes, item.callId, showReviewed],
    );

    const pendingFacts = facts.filter(
        (r) => r.reviewStatus == null || r.reviewStatus === 'pending',
    );
    const pendingActions = actions.filter(
        (r) => r.reviewStatus == null || r.reviewStatus === 'pending',
    );
    const pendingNotes = notes.filter(
        (r) => r.reviewStatus == null || r.reviewStatus === 'pending',
    );
    const pendingTotal = pendingFacts.length + pendingActions.length + pendingNotes.length;

    async function approveAll(): Promise<void> {
        const calls: Array<Promise<unknown>> = [];
        for (const f of pendingFacts) calls.push(approveReviewable('call-extracted-fact', f.id));
        for (const a of pendingActions) calls.push(approveReviewable('call-action-item', a.id));
        for (const n of pendingNotes) calls.push(approveReviewable('call-kb-note', n.id));
        await Promise.all(calls);
        pushToast({ kind: 'success', message: `Approved ${pendingTotal} drafts` });
    }

    return (
        <div className="flex flex-col gap-4">
            {pendingTotal > 0 && (
                <div className="flex items-center justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void approveAll()}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve all pending ({pendingTotal})
                    </Button>
                </div>
            )}

            {facts.length === 0 && actions.length === 0 && notes.length === 0 && (
                <EmptyState title="Nothing left to review for this call." />
            )}

            {facts.length > 0 && (
                <SectionShell title="Extracted facts" count={facts.length}>
                    {facts.map((f) => (
                        <FactItem
                            key={f.id}
                            row={f}
                            onApprove={() => void approveReviewable('call-extracted-fact', f.id)}
                            onReject={(reason) =>
                                void rejectReviewable(
                                    'call-extracted-fact',
                                    f.id,
                                    reason || undefined,
                                )
                            }
                        />
                    ))}
                </SectionShell>
            )}

            {actions.length > 0 && (
                <SectionShell title="Action items" count={actions.length}>
                    {actions.map((a) => (
                        <ActionItem
                            key={a.id}
                            row={a}
                            onApprove={() => void approveReviewable('call-action-item', a.id)}
                            onReject={(reason) =>
                                void rejectReviewable('call-action-item', a.id, reason || undefined)
                            }
                        />
                    ))}
                </SectionShell>
            )}

            {notes.length > 0 && (
                <SectionShell title="KB notes" count={notes.length}>
                    {notes.map((n) => (
                        <NoteItem
                            key={n.id}
                            row={n}
                            onApprove={() => void approveReviewable('call-kb-note', n.id)}
                            onReject={(reason) =>
                                void rejectReviewable('call-kb-note', n.id, reason || undefined)
                            }
                        />
                    ))}
                </SectionShell>
            )}
        </div>
    );
}

function SectionShell({
    title,
    count,
    children,
}: {
    title: string;
    count: number;
    children: React.ReactNode;
}) {
    return (
        <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <h3 className="text-card-title text-[rgb(var(--color-text-primary))]">{title}</h3>
                <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                    {String(count).padStart(2, '0')}
                </span>
            </div>
            <ul className="flex flex-col gap-2">{children}</ul>
        </section>
    );
}

function StatusBadge({ status }: { status: ReviewStatus | null | undefined }) {
    if (status === 'approved') {
        return <Pill variant="green" size="sm" label="approved" />;
    }
    if (status === 'rejected') {
        return <Pill variant="red" size="sm" label="rejected" />;
    }
    return <Pill variant="amber" size="sm" label="pending" />;
}

interface DecisionRowProps {
    status: ReviewStatus | null | undefined;
    /** Short label for the rejection dialog subject (e.g. fact text, action title). */
    subject?: string;
    onApprove: () => void;
    onReject: (reason: string) => void;
}

function DecisionButtons({ status, subject, onApprove, onReject }: DecisionRowProps) {
    const [rejecting, setRejecting] = useState(false);
    if (status != null && status !== 'pending') return null;
    return (
        <>
            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onApprove}
                    aria-label="Approve"
                    title="Approve"
                    icon={<ThumbsUp className="w-3.5 h-3.5" />}
                />
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRejecting(true)}
                    aria-label="Reject"
                    title="Reject"
                    icon={<ThumbsDown className="w-3.5 h-3.5" />}
                />
            </div>
            <RejectionDialog
                open={rejecting}
                subject={subject}
                onConfirm={(reason) => onReject(reason)}
                onClose={() => setRejecting(false)}
            />
        </>
    );
}

function FactItem({
    row,
    onApprove,
    onReject,
}: {
    row: FactRow;
    onApprove: () => void;
    onReject: (reason: string) => void;
}) {
    return (
        <li className="rounded-card border border-[rgb(var(--color-divider))] px-3 py-2.5 flex flex-col gap-1.5">
            <div className="flex items-start gap-2 flex-wrap">
                <Pill variant="grey" size="sm" label={row.category} />
                <Pill
                    variant={confidenceVariant(row.confidence)}
                    size="sm"
                    label={`${row.confidence} confidence`}
                />
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.text}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <DecisionButtons
                    status={row.reviewStatus}
                    subject={row.text}
                    onApprove={onApprove}
                    onReject={onReject}
                />
            </div>
            {row.source_quote && (
                <div className="flex items-start gap-1.5 text-annotation italic text-[rgb(var(--color-text-secondary))]">
                    <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.source_quote}</span>
                </div>
            )}
            {row.compliance_relevant && (
                <div className="flex items-start gap-1.5 text-annotation text-[rgb(var(--color-rag-amber))]">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.compliance_reason ?? 'Compliance-relevant'}</span>
                </div>
            )}
        </li>
    );
}

function confidenceVariant(c: 'high' | 'medium' | 'low'): 'green' | 'amber' | 'red' {
    return c === 'high' ? 'green' : c === 'medium' ? 'amber' : 'red';
}

function ActionItem({
    row,
    onApprove,
    onReject,
}: {
    row: ActionRow;
    onApprove: () => void;
    onReject: (reason: string) => void;
}) {
    return (
        <li className="rounded-card border border-[rgb(var(--color-divider))] px-3 py-2.5 flex flex-col gap-1">
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <DecisionButtons
                    status={row.reviewStatus}
                    subject={row.title}
                    onApprove={onApprove}
                    onReject={onReject}
                />
            </div>
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                {row.owner} · {row.due_description}
                {row.note ? ` · ${row.note}` : ''}
            </div>
        </li>
    );
}

function NoteItem({
    row,
    onApprove,
    onReject,
}: {
    row: NoteRow;
    onApprove: () => void;
    onReject: (reason: string) => void;
}) {
    return (
        <li className="rounded-card border border-[rgb(var(--color-divider))] px-3 py-2.5 flex flex-col gap-1.5">
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <DecisionButtons
                    status={row.reviewStatus}
                    subject={row.title}
                    onApprove={onApprove}
                    onReject={onReject}
                />
            </div>
            <CitedSummary body={row.body} callId={row.callId} className="text-annotation" />
        </li>
    );
}

// -------- Monthly narrative body --------

export function MonthlyNarrativeBody({
    item,
    showReviewed,
}: {
    item: MonthlyNarrativeReviewItem;
    showReviewed: boolean;
}) {
    const { data: draftsData } = useQuery<NarrativeDraftRow[]>({
        queryKey: ['agent', 'rows', 'wealth_monthly_narrative_drafts'],
        queryFn: async () => {
            const res = await api.agent.listAgentRows({
                params: { table: 'wealth_monthly_narrative_drafts' },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows as unknown as NarrativeDraftRow[];
        },
        refetchInterval: 5_000,
    });
    const allDrafts = draftsData ?? [];
    const { pushToast } = useToast();
    const draft = useMemo(
        () => allDrafts.find((d) => d.id === item.draftId),
        [allDrafts, item.draftId],
    );
    const [activeTone, setActiveTone] = useState<string | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const [requestingRevision, setRequestingRevision] = useState(false);

    if (!draft) {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft data unavailable — it may have just been removed.
            </div>
        );
    }

    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                Reviewed. Toggle "Show reviewed" above to see decided drafts.
            </div>
        );
    }

    const currentTone =
        activeTone ??
        draft.variants[draft.selectedVariantIndex]?.tone ??
        draft.variants[0]?.tone ??
        'plain';
    const variant = draft.variants.find((v) => v.tone === currentTone) ?? draft.variants[0];

    async function handleApprove(): Promise<void> {
        if (!variant) return;
        const idx = draft!.variants.findIndex((v) => v.tone === currentTone);
        const result = await approveReviewable('monthly-narrative-draft', draft!.id, {
            selectedVariantIndex: idx >= 0 ? idx : 0,
            performanceSummary: variant.performanceSummary,
            narrativeParagraph: variant.narrativeParagraph,
        });
        if (result.ok) pushToast({ kind: 'success', message: 'Narrative approved' });
    }

    async function handleReject(reason: string): Promise<void> {
        const result = await rejectReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || undefined,
        );
        if (result.ok) pushToast({ kind: 'success', message: 'Narrative rejected' });
    }

    async function handleRequestRevision(reason: string): Promise<void> {
        const result = await requestRevisionReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || 'No reason given',
        );
        if (result.ok)
            pushToast({ kind: 'success', message: 'Revision requested — ATLAS will re-roll' });
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={draft.reviewStatus} />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {draft.kbNotesCited.length} KB note
                    {draft.kbNotesCited.length === 1 ? '' : 's'} cited
                </span>
            </div>
            <div className="flex gap-1 border-b border-[rgb(var(--color-divider))]">
                {draft.variants.map((v) => (
                    <button
                        key={v.tone}
                        type="button"
                        onClick={() => setActiveTone(v.tone)}
                        className={cn(
                            'px-3 py-1.5 text-annotation font-medium border-b-2 -mb-px',
                            currentTone === v.tone
                                ? 'border-[rgb(var(--color-accent))] text-[rgb(var(--color-accent))]'
                                : 'border-transparent text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        {v.tone}
                    </button>
                ))}
            </div>
            {variant && (
                <div className="flex flex-col gap-2">
                    <div className="text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                        {variant.performanceSummary}
                    </div>
                    <div className="text-body-medium text-[rgb(var(--color-text-secondary))] whitespace-pre-wrap">
                        {variant.narrativeParagraph}
                    </div>
                </div>
            )}
            {(draft.reviewStatus == null || draft.reviewStatus === 'pending') && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRejecting(true)}
                        icon={<ThumbsDown className="w-3.5 h-3.5" />}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRequestingRevision(true)}
                        icon={<RotateCcw className="w-3.5 h-3.5" />}
                    >
                        Request revision
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleApprove()}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve {currentTone}
                    </Button>
                </div>
            )}
            <RejectionDialog
                open={rejecting}
                title="Reject narrative?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Optional — note why so ATLAS can re-roll."
                onConfirm={(reason) => void handleReject(reason)}
                onClose={() => setRejecting(false)}
            />
            <RejectionDialog
                open={requestingRevision}
                tone="neutral"
                title="Request revision?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Tell ATLAS what to change so it can re-roll the narrative."
                confirmLabel="Request revision"
                onConfirm={(reason) => void handleRequestRevision(reason)}
                onClose={() => setRequestingRevision(false)}
            />
        </div>
    );
}

// -------- Composer draft body (generic <CompositionWorkbench>) --------
//
// Renders the per-agent composition-workbench wrapper for a pending
// `*Draft` row. The draft row is re-fetched here by id from its table so
// the workbench gets the full envelope (brief / sources / plan / variants
// / handoff / citations). Approve / reject / request-revision mutations are
// owned by the generic <CompositionWorkbench>.

export function ComposerDraftBody({
    item,
    showReviewed,
}: {
    item: ComposerDraftReviewItem;
    showReviewed: boolean;
}) {
    const { data: rowsData } = useQuery<BaseDraft[]>({
        queryKey: ['agent', 'rows', item.table],
        queryFn: async () => {
            const res = await api.agent.listAgentRows({
                params: { table: item.table },
                query: {},
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows as unknown as BaseDraft[];
        },
        refetchInterval: 5_000,
    });
    const rows = rowsData ?? [];
    const draft = useMemo(
        () => rows.find((d) => d.id === item.draftId) ?? null,
        [rows, item.draftId],
    );

    if (!draft) {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft no longer available.
            </p>
        );
    }
    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                This draft has been reviewed.
            </p>
        );
    }

    switch (item.agent) {
        case 'SCRIVENER':
            return <ScrivenerWorkbench draft={draft} />;
        case 'HERALD':
            return <HeraldWorkbench draft={draft} />;
        case 'EMISSARY':
            return <EmissaryWorkbench draft={draft} />;
        case 'CHARTER':
            return <CharterWorkbench draft={draft} />;
        case 'HAVEN':
            return <HavenWorkbench draft={draft} />;
        case 'FORUM':
            return <ForumWorkbench draft={draft} />;
        case 'ARBITER':
            return <ArbiterWorkbench draft={draft} />;
    }
}

// -------- Task body (generic AgentTask review_required) --------

export function TaskBody({ item }: { item: TaskReviewItem }) {
    const { reviewTask } = useAgent();
    const task = item.task;
    const summary = describeTaskOutput(task);
    const [rejecting, setRejecting] = useState(false);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status="pending" />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {item.secondaryLabel}
                </span>
            </div>
            {summary && (
                <p className="text-body-medium text-[rgb(var(--color-text-secondary))] leading-snug">
                    {summary}
                </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                <DownloadRow task={task} />
                <ExportToSharePointButton task={task} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                <Link
                    to={item.contextHref}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-annotation rounded-button',
                        'border border-[rgb(var(--color-divider))]',
                        'text-[rgb(var(--color-text-primary))]',
                        'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                        'transition-colors duration-card-hover ease-swiss',
                    )}
                >
                    <FileText className="w-3.5 h-3.5" />
                    Open full draft
                </Link>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRejecting(true)}
                    icon={<XCircle className="w-3.5 h-3.5" />}
                >
                    Reject
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => reviewTask(task.id, 'approve')}
                    icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                >
                    Approve
                </Button>
            </div>
            <RejectionDialog
                open={rejecting}
                subject={`${item.primaryLabel} · ${item.secondaryLabel}`}
                onConfirm={(reason) => reviewTask(task.id, 'reject', reason || undefined)}
                onClose={() => setRejecting(false)}
            />
        </div>
    );
}

// One-line summary of a task's result, derived from its primary produced
// artefact row (PRD #261 follow-up — the legacy `output` union is no longer
// populated by compose). `summariseArtefact` reads the shared content fields
// (summary / headline / subject / claim / variant body) defensively.
export function describeTaskOutput(task: AgentTask): string | null {
    const row = task.artefacts?.[0]?.row;
    return row ? summariseArtefact(row) : null;
}
